import type { AssetApprovalChain, AssetApprovalChainStep } from '@/entities/asset-approval-chain';

export type ApprovalMenuItemKind = 'approve' | 'override' | 'approved';

export interface ApprovalMenuItem {
  stepId: string;
  stepName: string;
  kind: ApprovalMenuItemKind;
}

function itemForStep(
  step: AssetApprovalChainStep,
  userId: string | undefined,
  canOverride: boolean,
): ApprovalMenuItemKind | null {
  const isAssignee = !!userId && step.assignees.some((assignee) => assignee.userId === userId);
  const hasApproved = !!userId && step.approvals.some((approval) => approval.userId === userId);
  // Anyone the step does not name who approved it can only have done so through its role.
  const approvedThroughRole = hasApproved && !isAssignee && !!step.approverRole;

  if (step.state === 'met') return isAssignee || approvedThroughRole ? 'approved' : null;
  if (step.state !== 'actionable') return null;

  if (isAssignee) return hasApproved ? 'approved' : 'approve';
  if (approvedThroughRole) return 'approved';
  // The server's answer covers holders of the step's role, whom the assignee list never names.
  if (step.canCurrentUserApprove) return 'approve';
  return canOverride ? 'override' : null;
}

export function approvalMenuItemsFor(
  chain: AssetApprovalChain | null | undefined,
  userId: string | undefined,
  canOverride: boolean,
): ApprovalMenuItem[] {
  if (!chain) return [];

  return [...chain.workflowDefinition.steps]
    .sort((a, b) => a.position - b.position)
    .flatMap((step) => {
      const kind = itemForStep(step, userId, canOverride);
      return kind ? [{ stepId: step.id, stepName: step.name, kind }] : [];
    });
}
